import React from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Button } from '@ui/components/ui/button';
import { PhilBotApp, PhillConnections } from '@data/utils/types';
import { PHIL_BOT_APPS } from './config';

interface ConnectionsProps {
  connections?: PhillConnections;
  /** Sends the browser off to the source's consent screen. */
  onConnect: (app: PhilBotApp) => void;
  /** Withdraws the grant. Immediate — there is nobody else to ask. */
  onDisconnect: (app: PhilBotApp) => void;
  pending?: PhilBotApp | null;
  disabled?: boolean;
}

// The activity sources as connect/disconnect rows. Each stands on its own: Phill finds your Slack id
// from your email when the membership is made, so linking Slack is about letting it read what you
// posted, not about whether it can reach you.
const Connections = ({ connections, onConnect, onDisconnect, pending, disabled }: ConnectionsProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      className={cx('space-y-2 transition-opacity duration-300 ease-out', disabled && 'pointer-events-none opacity-50')}
    >
      {PHIL_BOT_APPS.map(app => {
        const connected = connections?.[app.key] ?? false;
        const Icon = app.icon;
        return (
          <div key={app.key} className="flex items-center gap-3 rounded-lg border border-border p-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Icon className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{app.label}</p>
              <p className="text-xs text-muted-foreground">{t(app.descriptionKey)}</p>
            </div>
            {connected ? (
              <div className="flex shrink-0 items-center gap-2 duration-200 animate-in fade-in-0 zoom-in-95">
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  <Check className="size-3.5" />
                  {t('philBot.connected')}
                </span>
                <Button variant="ghost" size="sm" disabled={pending === app.key} onClick={() => onDisconnect(app.key)}>
                  {t('philBot.disconnect')}
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 duration-200 animate-in fade-in-0"
                onClick={() => onConnect(app.key)}
              >
                {t('philBot.connect')}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Connections;
